import { Link } from "react-router-dom";
import { Camera, MessagesSquare, BadgeCheck, KeyRound, Star, ArrowRight, Hammer, Zap, Droplets, PaintRoller, ShieldCheck, Smartphone } from "lucide-react";
import { useMe } from "@/context/Me";
import { Tape, CountUp } from "@/components/bits";
import { DEFAULT_STORY, money, specLabel } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1717281234297-3def5ae3eee1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const STEPS = [
  { icon: Camera, t: "Envie fotos", d: "Até 3 fotos e uma frase. A avaliação técnica gera o diagnóstico: causa, urgência, materiais e custo." },
  { icon: BadgeCheck, t: "Receba propostas", d: "Profissionais verificados da sua região enviam valor e prazo. Compare nota, distância e selos." },
  { icon: KeyRound, t: "Conclua com o código", d: "No fim do serviço, passe o código de 6 dígitos. Só com ele o serviço é concluído." },
];
const SAMPLE = {
  BR: { items: [["Impermeabilizante acrílico 3,6L", "R$ 90 – 140"], ["Massa corrida 5kg", "R$ 35 – 55"], ["Tinta antimofo 3,6L", "R$ 110 – 180"], ["Mão de obra (6h)", "R$ 300 – 450"]], total: "R$ 535 – 825" },
  PT: { items: [["Impermeabilizante acrílico 4L", "€ 25 – 40"], ["Massa de reparação 5kg", "€ 10 – 18"], ["Tinta anti-humidade 4L", "€ 30 – 50"], ["Mão de obra (6h)", "€ 150 – 240"]], total: "€ 215 – 348" },
};
const SPECS = [[Hammer, "Pedreiro"], [Zap, "Eletricista"], [Droplets, "Encanador"], [PaintRoller, "Pintor"], [ShieldCheck, "Serralheiro"]];

export default function Landing() {
  const { meta, country } = useMe();
  const story = (meta?.story || DEFAULT_STORY).split("\n\n");
  const p = meta?.prices?.[country];
  const sample = SAMPLE[country] || SAMPLE.BR;
  const isPT = country === "PT";
  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111317] via-[#111317]/90 to-[#111317]/40" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:py-24">
          <div className="fade-up">
            <div className="eyebrow mb-4" data-testid="landing-country">{isPT ? "🇵🇹 Portugal" : "🇧🇷 Brasil"}</div>
            <h1 className="text-5xl font-black uppercase leading-[0.92] sm:text-6xl lg:text-7xl">
              Sua obra,<br /><span className="text-[#FFC700]">sem dor</span><br />de cabeça.
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg">
              Envie uma foto e receba o diagnóstico da obra. O preço justo deixa de ser mistério. E o bom profissional verificado está perto de {isPT ? "si" : "casa"}.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/diagnostico" data-testid="hero-photo-cta" className="btn-hazard px-6 py-4 text-lg"><Camera /> Avaliação técnica</Link>
              <Link to="/assistente" data-testid="hero-assistant-cta" className="btn-ghost px-6 py-4 text-lg"><MessagesSquare /> Falar com o assistente</Link>
            </div>
            <div className="mt-10 grid max-w-md grid-cols-3 gap-4">
              {[["Profissionais", meta?.stats_by_country?.[country]?.pros || 0], ["Chamados", meta?.stats_by_country?.[country]?.jobs || 0], ["Concluídos", meta?.stats_by_country?.[country]?.completed || 0]].map(([l, v]) => (
                <div key={l}><div className="font-display text-4xl font-black text-[#FFC700]"><CountUp value={v} /></div><div className="text-xs uppercase tracking-wider text-slate-400">{l}</div></div>
              ))}
            </div>
          </div>
          <div className="fade-up hidden lg:block" style={{ animationDelay: "0.15s" }}>
            <div className="panel relative rotate-1 p-5 shadow-2xl">
              <div className="tape-thin absolute -top-3 left-6 right-6 h-3 rounded" />
              <div className="eyebrow mb-3">Diagnóstico da obra · exemplo</div>
              <h3 className="text-3xl font-bold uppercase">Infiltração na parede</h3>
              <span className="mt-2 inline-block rounded-full bg-[#FF5722]/15 px-3 py-1 text-xs font-bold text-[#FF5722]">Urgência alta</span>
              <div className="mt-4 space-y-2 text-sm">
                {sample.items.map(([a, b]) => (
                  <div key={a} className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-300">{a}</span><span className="font-mono font-bold">{b}</span></div>
                ))}
              </div>
              <div className="mt-4 flex items-end justify-between">
                <span className="text-xs uppercase text-slate-400">Valor aproximado</span>
                <span className="font-display text-4xl font-black text-[#FFC700]" data-testid="landing-sample-total">{sample.total}</span>
              </div>
            </div>
          </div>
        </div>
        <Tape />
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="eyebrow mb-2">Como funciona</div>
        <h2 className="mb-10 text-4xl font-black uppercase sm:text-5xl">3 passos. Zero enrolação.</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.t} className="panel panel-hover relative p-6 fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <span className="absolute right-5 top-4 font-display text-6xl font-black text-white/5">0{i + 1}</span>
              <div className="mb-4 inline-flex rounded-xl bg-[#FFC700] p-3 text-[#111317]"><s.icon /></div>
              <h3 className="text-2xl font-bold uppercase">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-400">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          {SPECS.map(([I, l]) => (
            <span key={l} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300"><I size={16} className="text-[#FFC700]" />{specLabel(l, country)}</span>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#181B20]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <div className="eyebrow mb-2">Nossa história</div>
            <h2 className="text-4xl font-black uppercase sm:text-5xl">Tudo começou com uma infiltração.</h2>
            <Link to="/historia" data-testid="story-read-more" className="mt-6 inline-flex items-center gap-2 font-bold text-[#FFC700] hover:underline">Ler a história completa <ArrowRight size={18} /></Link>
          </div>
          <div className="space-y-4 text-slate-300">
            {story.slice(0, 3).map((para, i) => <p key={i} className={i === 0 ? "text-lg" : ""}>{para}</p>)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="panel p-8">
            <div className="eyebrow mb-2">Para profissionais</div>
            <h3 className="text-3xl font-black uppercase">O trabalho chega até você.</h3>
            <p className="mt-3 text-slate-400">Mural com mapa de chamados perto de você, perfil com fotos e avaliações, selo Verificado. Grátis com 3 propostas/mês.</p>
            <div className="mt-5 flex flex-wrap gap-3 text-sm">
              <span className="rounded-xl border border-purple-500/30 bg-purple-500/10 px-4 py-2 text-purple-200" data-testid="landing-pro-price">Pro: {money(p?.pro ?? (isPT ? 9.9 : 39.9), country)}/mês</span>
              {p?.featured ? <span className="rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-2 text-orange-200" data-testid="landing-featured-price">Destaque 7 dias: {money(p.featured, country)}</span> : null}
            </div>
            <Link to="/sign-up" data-testid="pro-signup-cta" className="btn-hazard mt-6">Quero ser profissional <ArrowRight size={18} /></Link>
          </div>
          <div className="panel flex flex-col justify-between p-8">
            <div>
              <div className="eyebrow mb-2">App no celular</div>
              <h3 className="text-3xl font-black uppercase">Instale no Android e iPhone.</h3>
              <p className="mt-3 text-slate-400">Abra o Zelobra no navegador e toque em “Adicionar à tela inicial”. Ele abre em tela cheia, como um app.</p>
            </div>
            <div className="mt-6 flex items-center gap-4"><Smartphone size={44} className="text-[#FFC700]" /><div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className="fill-[#FFC700] text-[#FFC700]" size={18} />)}</div></div>
          </div>
        </div>
      </section>
      <footer className="border-t border-white/10 py-8 text-center text-sm text-slate-500">Zelobra © {new Date().getFullYear()} · Sua obra, sem dor de cabeça.</footer>
    </div>
  );
}
